// /var/www/speakiq/frontend/src/pages/admin/AdminStudents.js
import React, { useState, useEffect, useMemo } from 'react';
import Layout from '../../components/Layout';
import api from '../../utils/api';
import AccountMgmtDropdown from '../../components/AccountMgmtDropdown';

const C = {
  navy: '#0D1B2A', teal: '#00B4D8', success: '#10B981',
  warning: '#F59E0B', error: '#EF4444', grey: '#6B7280', border: '#E5E7EB',
};

function statusBadge(status) {
  const map = {
    active:    { bg: '#D1FAE5', color: C.success,   label: 'Active' },
    pending:   { bg: '#FEF9C3', color: '#92400E',   label: 'Pending' },
    suspended: { bg: '#FEF3C7', color: C.warning,   label: 'Suspended' },
    blocked:   { bg: '#FEE2E2', color: C.error,     label: 'Blocked' },
    deleted:   { bg: '#F3F4F6', color: '#7F1D1D',   label: 'Deleted' },
  };
  const s = map[status] || map.active;
  return (
    <span style={{ background: s.bg, color: s.color, fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20, letterSpacing: 0.5 }}>
      {s.label}
    </span>
  );
}

function cefrBg(level) {
  if (!level) return C.grey;
  const l = level.toUpperCase();
  if (l === 'C1' || l === 'C2') return '#D97706';
  if (l === 'B1' || l === 'B2') return C.teal;
  return '#64748B';
}

function Avatar({ student }) {
  if (student.photo_url) {
    return <img src={student.photo_url} alt="" style={{ width: 36, height: 36, borderRadius: '50%', objectFit: 'cover' }} />;
  }
  const initials = (student.full_name || '?').split(' ').map(p => p[0]).join('').slice(0, 2).toUpperCase();
  return (
    <div style={{ width: 36, height: 36, borderRadius: '50%', background: C.teal, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 13 }}>
      {initials}
    </div>
  );
}

const STATUS_TABS = ['all', 'active', 'pending', 'suspended', 'blocked'];

// Correctly resolves a student's status from account_status field.
// Blocked/deleted/suspended always win over the is_active flag.
function getStatus(s) {
  const as = s.account_status;
  if (as === 'blocked' || as === 'suspended' || as === 'deleted') return as;
  return s.is_active ? 'active' : 'pending';
}

export default function AdminStudents() {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [curriculumModal, setCurriculumModal] = useState(null); // student object + progress data
  const [curriculumLoading, setCurriculumLoading] = useState(false);

  const openCurriculum = async (student) => {
    setCurriculumModal({ student, progress: null });
    setCurriculumLoading(true);
    try {
      const r = await api.get(`/api/admin/students/${student.id}/curriculum-progress`);
      setCurriculumModal({ student, progress: r.data });
    } catch {
      setCurriculumModal({ student, progress: null, error: true });
    } finally {
      setCurriculumLoading(false);
    }
  };

  const fetchStudents = () => {
    setLoading(true);
    api.get('/api/admin/students')
      .then(res => setStudents(res.data?.students || res.data || []))
      .catch(() => setError('Failed to load students.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchStudents(); }, []);

  const filtered = useMemo(() => {
    // Exclude deleted students from all views (no deleted tab)
    let list = students.filter(s => getStatus(s) !== 'deleted');
    if (statusFilter !== 'all') {
      list = list.filter(s => getStatus(s) === statusFilter);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(s =>
        (s.full_name || '').toLowerCase().includes(q) ||
        (s.email || '').toLowerCase().includes(q)
      );
    }
    return list;
  }, [students, statusFilter, search]);

  const tabCounts = useMemo(() => {
    const visible = students.filter(s => getStatus(s) !== 'deleted');
    const counts = { all: visible.length };
    STATUS_TABS.slice(1).forEach(t => {
      counts[t] = visible.filter(s => getStatus(s) === t).length;
    });
    return counts;
  }, [students]);

  const thStyle = { textAlign: 'left', padding: '12px 14px', fontSize: 12, fontWeight: 700, color: C.grey, borderBottom: '2px solid ' + C.border, background: '#F9FAFB', whiteSpace: 'nowrap' };
  const tdStyle = { padding: '12px 14px', fontSize: 14, color: C.navy, borderBottom: '1px solid ' + C.border, verticalAlign: 'middle' };

  return (
    <Layout role="tenant_admin">
      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '28px 16px' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 700, color: C.navy, margin: 0 }}>Students</h1>
            <p style={{ color: C.grey, fontSize: 14, margin: '2px 0 0' }}>
              Showing {filtered.length} of {tabCounts.all} students
            </p>
          </div>
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by name or email…"
            style={{ padding: '9px 14px', borderRadius: 8, border: '1px solid ' + C.border, fontSize: 14, width: 240, outline: 'none' }}
          />
        </div>

        {/* Status filter tabs */}
        <div style={{ display: 'flex', gap: 4, marginBottom: 20, borderBottom: '2px solid ' + C.border }}>
          {STATUS_TABS.map(t => (
            <button key={t} onClick={() => setStatusFilter(t)}
              style={{
                background: 'none', border: 'none', padding: '8px 16px', cursor: 'pointer',
                fontSize: 14, fontWeight: 600, textTransform: 'capitalize',
                color: statusFilter === t ? C.teal : C.grey,
                borderBottom: statusFilter === t ? '2px solid ' + C.teal : '2px solid transparent',
                marginBottom: -2,
              }}>
              {t} <span style={{ fontSize: 11, background: '#F3F4F6', borderRadius: 10, padding: '1px 7px', marginLeft: 4 }}>{tabCounts[t] || 0}</span>
            </button>
          ))}
        </div>

        {error && <div style={{ background: '#FEE2E2', color: C.error, padding: '12px 16px', borderRadius: 8, marginBottom: 16 }}>{error}</div>}

        {loading ? (
          <div style={{ textAlign: 'center', padding: 60, color: C.grey }}>Loading students…</div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 60, color: C.grey }}>No students found.</div>
        ) : (
          <div style={{ overflowX: 'auto', borderRadius: 10, border: '1px solid ' + C.border, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={thStyle}>Student</th>
                  <th style={thStyle}>Email</th>
                  <th style={thStyle}>Assessment</th>
                  <th style={thStyle}>Goal</th>
                  <th style={thStyle}>Package</th>
                  <th style={thStyle}>Mode</th>
                  <th style={thStyle}>Status</th>
                  <th style={thStyle}>Joined</th>
                  <th style={{ ...thStyle, textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(student => {
                  const st = getStatus(student);
                  return (
                    <tr key={student.id} style={{ background: '#fff' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#F9FAFB'}
                      onMouseLeave={e => e.currentTarget.style.background = '#fff'}>
                      <td style={tdStyle}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <Avatar student={student} />
                          <span style={{ fontWeight: 600 }}>{student.full_name}</span>
                        </div>
                      </td>
                      <td style={{ ...tdStyle, color: C.grey }}>{student.email}</td>
                      <td style={tdStyle}>
                        {student.cefr_level ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                            <span style={{ background: cefrBg(student.cefr_level), color: '#fff', fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 4, display: 'inline-block' }}>{student.cefr_level}</span>
                            {student.assessment_completed_at && <span style={{ fontSize: 11, color: C.grey }}>{new Date(student.assessment_completed_at).toLocaleDateString('en-GB')}</span>}
                          </div>
                        ) : (
                          <span style={{ color: C.grey, fontSize: 12 }}>Not taken</span>
                        )}
                      </td>
                      <td style={tdStyle}>
                        {student.learning_goal ? (
                          <span style={{ fontSize: 12, color: C.teal, fontWeight: 600, textTransform: 'capitalize' }}>{student.learning_goal}</span>
                        ) : <span style={{ color: C.grey, fontSize: 12 }}>—</span>}
                      </td>
                      <td style={{ ...tdStyle, color: C.grey }}>{student.package_name || '—'}</td>
                      <td style={tdStyle}>
                        {student.learning_mode === 'physical'
                          ? <span style={{ background: '#D1FAE5', color: '#065F46', fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20 }}>🏫 Physical</span>
                          : <span style={{ background: '#E0F2FE', color: '#0369A1', fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20 }}>🌐 Remote</span>
                        }
                      </td>
                      <td style={tdStyle}>{statusBadge(st)}</td>
                      <td style={{ ...tdStyle, color: C.grey, fontSize: 13 }}>
                        {student.joined_at ? new Date(student.joined_at).toLocaleDateString('en-GB') : '—'}
                      </td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>
                        <div style={{ display:'flex', gap:6, justifyContent:'flex-end', alignItems:'center' }}>
                          <button onClick={() => openCurriculum(student)} style={{ padding:'5px 10px', borderRadius:8, border:'1px solid #6366F1', background:'#EEF2FF', color:'#6366F1', fontSize:11, fontWeight:600, cursor:'pointer' }}>
                            📚 Curriculum
                          </button>
                          <AccountMgmtDropdown
                            targetType="student"
                            targetId={student.id}
                            targetName={student.full_name}
                            currentStatus={st}
                            apiBase="/api/admin/students"
                            onActionComplete={fetchStudents}
                          />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Curriculum Progress Modal ── */}
      {curriculumModal && (() => {
        const { student, progress, error } = curriculumModal;
        const SKILL_COLORS = {grammar:'#6366F1',vocabulary:'#059669',reading:'#F59E0B',writing:'#EF4444',speaking:'#00B4D8'};
        const SKILL_BG = {grammar:'#EEF2FF',vocabulary:'#ECFDF5',reading:'#FFFBEB',writing:'#FFF1F2',speaking:'#F0F7FA'};
        return (
          <div onClick={() => setCurriculumModal(null)} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000, padding:16 }}>
            <div onClick={e => e.stopPropagation()} style={{ background:'#fff', borderRadius:16, padding:28, width:'100%', maxWidth:600, maxHeight:'90vh', overflowY:'auto', boxShadow:'0 8px 40px rgba(0,0,0,0.25)' }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
                <div>
                  <h3 style={{ margin:0, fontSize:17, fontWeight:700, color:C.navy }}>📚 Curriculum Progress</h3>
                  <div style={{ fontSize:13, color:C.grey, marginTop:2 }}>{student.full_name} · Level {student.cefr_level}</div>
                </div>
                <button onClick={() => setCurriculumModal(null)} style={{ background:'none', border:'none', fontSize:22, cursor:'pointer', color:C.grey }}>×</button>
              </div>

              {curriculumLoading ? (
                <div style={{ textAlign:'center', padding:40, color:C.grey }}>Loading curriculum data...</div>
              ) : error ? (
                <div style={{ background:'#FEE2E2', color:C.error, borderRadius:8, padding:'12px 16px' }}>Failed to load curriculum progress.</div>
              ) : progress ? (
                <>
                  {/* Overall bar */}
                  <div style={{ background:'#F9FAFB', borderRadius:10, padding:'14px 16px', marginBottom:20 }}>
                    <div style={{ display:'flex', justifyContent:'space-between', marginBottom:8, fontSize:14 }}>
                      <span style={{ fontWeight:600, color:C.navy }}>{progress.covered} of {progress.total_materials} topics covered</span>
                      <span style={{ fontWeight:800, fontSize:16, color:'#10B981' }}>{progress.coverage_percent}%</span>
                    </div>
                    <div style={{ height:10, background:'#E5E7EB', borderRadius:5 }}>
                      <div style={{ height:10, background:'#10B981', borderRadius:5, width:`${progress.coverage_percent}%`, transition:'width 0.5s' }} />
                    </div>
                  </div>

                  {/* By skill */}
                  <div style={{ marginBottom:20 }}>
                    <div style={{ fontWeight:600, fontSize:13, color:C.grey, marginBottom:10 }}>BY SKILL TYPE</div>
                    <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(200px, 1fr))', gap:8 }}>
                      {Object.entries(progress.by_skill || {}).map(([skill, data]) => {
                        const pct = data.total ? Math.round(data.covered / data.total * 100) : 0;
                        return (
                          <div key={skill} style={{ background:SKILL_BG[skill]||'#F9FAFB', borderRadius:8, padding:'10px 14px' }}>
                            <div style={{ display:'flex', justifyContent:'space-between', marginBottom:6 }}>
                              <span style={{ fontWeight:600, fontSize:13, color:SKILL_COLORS[skill]||C.navy, textTransform:'capitalize' }}>{skill}</span>
                              <span style={{ fontSize:12, color:C.grey }}>{data.covered}/{data.total}</span>
                            </div>
                            <div style={{ height:6, background:'rgba(0,0,0,0.08)', borderRadius:3 }}>
                              <div style={{ height:6, background:SKILL_COLORS[skill]||'#10B981', borderRadius:3, width:`${pct}%` }} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Covered materials */}
                  {(progress.covered_materials || []).length > 0 && (
                    <div style={{ marginBottom:16 }}>
                      <div style={{ fontWeight:600, fontSize:13, color:C.grey, marginBottom:8 }}>COVERED ({progress.covered_materials.length})</div>
                      <div style={{ display:'flex', flexDirection:'column', gap:4, maxHeight:160, overflowY:'auto' }}>
                        {progress.covered_materials.map(m => (
                          <div key={m.id} style={{ display:'flex', alignItems:'center', gap:8, padding:'6px 10px', background:'#F0FDF4', borderRadius:8 }}>
                            <span style={{ color:'#10B981', fontWeight:700, fontSize:13 }}>✓</span>
                            <span style={{ flex:1, fontSize:13, color:'#065F46', fontWeight:500 }}>{m.title}</span>
                            <span style={{ fontSize:11, padding:'2px 6px', borderRadius:10, background:SKILL_BG[m.skill_type]||'#F3F4F6', color:SKILL_COLORS[m.skill_type]||C.grey, fontWeight:600 }}>{m.skill_type}</span>
                            {m.times_covered > 1 && <span style={{ fontSize:10, color:C.grey }}>×{m.times_covered}</span>}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Not covered materials */}
                  {(progress.not_covered || []).length > 0 && (
                    <div>
                      <div style={{ fontWeight:600, fontSize:13, color:C.grey, marginBottom:8 }}>NOT YET COVERED ({progress.not_covered.length})</div>
                      <div style={{ display:'flex', flexDirection:'column', gap:4, maxHeight:160, overflowY:'auto' }}>
                        {progress.not_covered.map(m => (
                          <div key={m.id} style={{ display:'flex', alignItems:'center', gap:8, padding:'6px 10px', background:'#F9FAFB', borderRadius:8, border:'1px solid #F3F4F6' }}>
                            <span style={{ color:'#D1D5DB', fontWeight:700, fontSize:13 }}>○</span>
                            <span style={{ flex:1, fontSize:13, color:C.navy }}>{m.title}</span>
                            <span style={{ fontSize:11, padding:'2px 6px', borderRadius:10, background:SKILL_BG[m.skill_type]||'#F3F4F6', color:SKILL_COLORS[m.skill_type]||C.grey, fontWeight:600 }}>{m.skill_type}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <div style={{ textAlign:'center', padding:40, color:C.grey }}>No curriculum data available yet.</div>
              )}
            </div>
          </div>
        );
      })()}
    </Layout>
  );
}
